import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import type { CleanupPolicy } from '@/lib/packshots';
import type { StageEvent } from '@/lib/packshotSocket';
import { cn } from '@/lib/utils';
import Checkerboard from './Checkerboard';
import StraightenControl from './StraightenControl';
import { frameIndexForStage, previewFrames, type FrameKind, type PreviewFrame } from './previewFrames';
import {
  STAGE_LABEL,
  railStatuses,
  visibleStages,
  type RailJob,
  type RailStatus,
  type StageId,
} from './railModel';

type Straighten = {
  lean: number;
  applied: number;
  canWrite: boolean;
  pending: boolean;
  onApply: (degrees: number) => void;
};

type Props = {
  job: RailJob;
  cleanupPolicy: CleanupPolicy;
  events: readonly StageEvent[];
  liveNote: string | null;
  straighten?: Straighten | null;
  notice?: ReactNode;
  children?: ReactNode;
};

function nodeClass(status: RailStatus): string {
  switch (status) {
    case 'pending':
      return 'border-border bg-muted text-muted-foreground';
    case 'active':
      return 'border-sky-600 bg-sky-600 text-white dark:border-sky-400 dark:bg-sky-500';
    case 'completed':
      return 'border-emerald-600 bg-emerald-600 text-white dark:border-emerald-400 dark:bg-emerald-500';
    case 'skipped':
      return 'border-dashed border-amber-600 bg-amber-50 text-amber-800 dark:border-amber-400 dark:bg-amber-950 dark:text-amber-200';
    case 'failed':
      return 'border-destructive bg-destructive text-destructive-foreground';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function labelClass(status: RailStatus): string {
  switch (status) {
    case 'pending':
      return 'text-muted-foreground';
    case 'active':
      return 'font-medium text-sky-700 dark:text-sky-300';
    case 'completed':
      return 'text-emerald-700 dark:text-emerald-300';
    case 'skipped':
      return 'text-amber-800 dark:text-amber-200';
    case 'failed':
      return 'text-destructive';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function pillBorderClass(status: RailStatus): string {
  switch (status) {
    case 'pending':
      return 'border-border';
    case 'active':
      return 'border-sky-600 dark:border-sky-400';
    case 'completed':
      return 'border-emerald-600 dark:border-emerald-400';
    case 'skipped':
      return 'border-amber-600 dark:border-amber-400';
    case 'failed':
      return 'border-destructive';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function connectorClass(status: RailStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-border';
    case 'active':
      return 'bg-sky-600 dark:bg-sky-400';
    case 'completed':
      return 'bg-emerald-600 dark:bg-emerald-400';
    case 'skipped':
      return 'bg-amber-600 dark:bg-amber-400';
    case 'failed':
      return 'bg-destructive';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function ownerStage(kind: FrameKind, stages: readonly StageId[]): StageId | null {
  if (kind === 'original' || kind === 'normalized') return stages.includes('ingest') ? 'ingest' : null;
  if (kind === 'erased') return stages.includes('props') ? 'props' : null;
  if (kind === 'cleaned') return stages.includes('cleanup') ? 'cleanup' : null;
  if (kind === 'cutout') return stages.includes('cutout') ? 'cutout' : null;
  if (kind === 'output') {
    if (stages.includes('compose')) return 'compose';
    return stages.includes('qa') ? 'qa' : null;
  }
  return null;
}

function originalFrame(frames: readonly PreviewFrame[]): PreviewFrame | null {
  return frames.find((frame) => frame.kind === 'original' || frame.kind === 'normalized') ?? null;
}

function ComparePane({
  caption,
  frame,
  empty,
  tilt,
}: {
  caption: string;
  frame: PreviewFrame | null;
  empty: string;
  tilt: number;
}) {
  const tilted = frame?.kind === 'output' && Math.abs(tilt) >= 0.05;
  return (
    <figure className="min-w-0">
      <Checkerboard className="relative mx-auto aspect-[480/655] w-full max-w-[calc((100dvh-16rem)*480/655)] rounded-lg">
        {frame?.kind === 'output' && <div aria-hidden="true" className="absolute inset-0 bg-black" />}
        {frame ? (
          <img
            src={frame.url}
            alt={caption}
            className="absolute inset-0 h-full w-full object-contain"
            style={tilted ? { transform: `rotate(${tilt}deg) scale(0.92)` } : undefined}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-stone-700">
            {empty}
          </div>
        )}
        {tilted && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <div className="absolute inset-x-0 top-1/3 border-t border-white/55" />
            <div className="absolute inset-x-0 top-2/3 border-t border-white/55" />
            <div className="absolute inset-y-0 left-1/3 border-l border-white/55" />
            <div className="absolute inset-y-0 left-2/3 border-l border-white/55" />
          </div>
        )}
      </Checkerboard>
    </figure>
  );
}

function statusText(status: RailStatus): string {
  switch (status) {
    case 'pending':
      return 'Waiting';
    case 'active':
      return 'In progress';
    case 'completed':
      return 'Done';
    case 'skipped':
      return 'Skipped';
    case 'failed':
      return 'Failed';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

export default function StageRail({ job, cleanupPolicy, events, liveNote, straighten, notice, children }: Props) {
  const stages = visibleStages(cleanupPolicy);
  const statuses = railStatuses(job, stages, events);
  const frames = useMemo(() => previewFrames(job), [job]);
  const [pinnedStage, setPinnedStage] = useState<StageId | null>(null);
  const [previewAngle, setPreviewAngle] = useState<number | null>(null);
  const onPreviewAngle = useCallback((degrees: number) => {
    setPreviewAngle(degrees);
  }, []);

  useEffect(() => {
    setPinnedStage(null);
    setPreviewAngle(null);
  }, [job.id]);

  function showOutput() {
    if (!frames.some((frame) => frame.kind === 'output')) return;
    setPinnedStage('compose');
  }

  function selectStage(stage: StageId) {
    if (frameIndexForStage(frames, stage) < 0) return;
    setPinnedStage(stage);
  }

  const pinnedIndex = pinnedStage ? frameIndexForStage(frames, pinnedStage) : -1;
  const packshot = pinnedIndex >= 0 ? frames[pinnedIndex] ?? null : frames[frames.length - 1] ?? null;
  const outputTilt = straighten && previewAngle !== null ? straighten.applied - previewAngle : 0;
  const selectedStage = pinnedStage ?? (packshot ? ownerStage(packshot.kind, stages) : null);
  const activeStage = stages.find((stage) => statuses[stage] === 'active') ?? null;
  const waiting = activeStage ? STAGE_LABEL[activeStage] : 'Waiting for the image';

  return (
    <div className="space-y-6">
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_18rem_minmax(0,1fr)]">
        <ComparePane caption="Original" frame={originalFrame(frames)} empty="Waiting for the image" tilt={0} />
        <div className="min-w-0 space-y-4">
          {notice}
          <ol className="mx-auto flex w-max max-w-full flex-col" aria-label="Processing stages">
            {stages.map((stage, index) => {
              const status = statuses[stage];
              const previous = index > 0 ? stages[index - 1] : null;
              const canOpen = frameIndexForStage(frames, stage) >= 0 && status !== 'pending';
              const selected = selectedStage === stage;
              return (
                <li key={stage} className="flex flex-col items-center">
                  {previous && (
                    <span
                      aria-hidden="true"
                      className={cn('h-5 w-0.5 shrink-0', connectorClass(statuses[previous]))}
                    />
                  )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canOpen}
                  aria-current={status === 'active' ? 'step' : undefined}
                  aria-pressed={selected}
                  aria-label={`${STAGE_LABEL[stage]}, ${statusText(status)}`}
                  className={cn('h-auto w-full justify-start gap-2 border-2 px-3 py-1.5', pillBorderClass(status), labelClass(status), selected && 'ring-1 ring-ring')}
                  onClick={() => selectStage(stage)}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-full border text-[11px]',
                      nodeClass(status),
                      status === 'active' && 'animate-pulse',
                    )}
                  >
                    {status === 'completed' ? '✓' : status === 'failed' ? '!' : status === 'skipped' ? '–' : ''}
                  </span>
                  {STAGE_LABEL[stage]}
                </Button>
              </li>
              );
            })}
          </ol>
          {straighten && (
            <StraightenControl
              lean={straighten.lean}
              applied={straighten.applied}
              canWrite={straighten.canWrite}
              pending={straighten.pending}
              onAngle={onPreviewAngle}
              onApply={straighten.onApply}
              onInteract={showOutput}
            />
          )}
          {liveNote && <p className="text-xs text-muted-foreground">{liveNote}</p>}
        </div>
        <ComparePane caption="Packshot" frame={packshot} empty={waiting} tilt={outputTilt} />
      </div>
      {children}
    </div>
  );
}
